import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Modal,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import { ZyppLiteApi } from "../../../API/Zypp-Lite-Api/LiteApi";
import axiosInstance from "../../../API/axiosInstance";

type FormData = {
  sk: string;
  key: string;
};

const CredentialsInfo = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [open, setOpen] = useState(false);
  const [isSuccessSnackbar, setIsSuccessSnackbar] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [initialUpdate, setInitialUpdate] = useState(false);

  // 👇 formData now ONLY has sk and key
  const [formData, setFormData] = useState<FormData>({
    sk: "",
    key: "",
  });

  // 👇 separate state for title (used only in update)
  const [paymentGatewayTitle, setPaymentGatewayTitle] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const response = await ZyppLiteApi.api?.getCredentialsInfo();

        if (response && Array.isArray(response.data) && response.data.length > 0) {
          const row = response.data[0];
          setFormData({
            sk: row.sk || "",
            key: row.key || "",
          });
          setPaymentGatewayTitle(row.tn || "");
          setIsUpdateMode(true);
        } else {
          setFormData({ sk: "", key: "" });
          setPaymentGatewayTitle("");
          setIsUpdateMode(false);
        }
      } catch (error) {
        console.error("API call error: ", error);
        setFormData({ sk: "", key: "" });
        setPaymentGatewayTitle("");
        setIsUpdateMode(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleBackToDashboard = () => navigate("/userdetails");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInitialUpdate(false);
    const { name, value } = e.target; // name will be "sk" or "key"
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInitialUpdate(false);
    setPaymentGatewayTitle(e.target.value);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.sk.trim()) newErrors.sk = "Secret Key is required";
    if (!formData.key.trim()) newErrors.key = "Encryption Key is required";
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) setIsSubmitting(true);
  };

  useEffect(() => {
    if (!isSubmitting) return;

    const submitData = async () => {
      try {
        const endpoint = isUpdateMode
          ? "/users/dashboard/update-payment-credentials"
          : "/users/dashboard/create-payment-credentials";

        // ✅ CREATE -> only sk, key
        // ✅ UPDATE -> sk, key, tn (mapped from paymentGatewayTitle)
        const payload = { sk: formData.sk, key: formData.key };

        const response = await axiosInstance.post(endpoint, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200) {
          setIsUpdateMode(true);
          setInitialUpdate(true);
          setOpen(true);
          setSnackbarMessage(isUpdateMode ? "Credentials updated successfully!" : "Credentials created successfully!");
          setIsSuccessSnackbar(true);

          setTimeout(() => {
            setOpen(false);
            navigate("/userdetails", { state: { tabIndex: 1 } });
          }, 3000);
        } else {
          setSnackbarMessage(response.data?.message || `Error ${isUpdateMode ? "updating" : "creating"} credentials`);
          setShowErrorSnackbar(true);
        }
      } catch (error) {
        console.error("API call error:", error);
        if (axios.isAxiosError(error) && error.response) {
          setSnackbarMessage(
            error.response.data?.message ||
              `Failed to ${isUpdateMode ? "update" : "create"} credentials. Please try again.`
          );
        } else {
          setSnackbarMessage("Network error or server unreachable.");
        }
        setShowErrorSnackbar(true);
      } finally {
        setIsSubmitting(false);
      }
    };

    submitData();
  }, [isSubmitting, isUpdateMode, formData, paymentGatewayTitle, token, navigate]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <div className="flex bg-white min-h-80 box-shadow p-3 rounded-xl mx-16">
        <div className="w-full">
          <Grid container spacing={5} className="w-full flex justify-between items-center">
            <Grid item md={6}>
              <div className="flex ml-4 item-center">
                <h2 className="text-center ml-2 mx-1 mt-2 font-semibold text-2xl text-black pb-2">
                  <IconButton onClick={handleBackToDashboard}>
                    <ArrowBackIcon />
                  </IconButton>
                  {isUpdateMode ? "Update Credentials" : "Create Credentials"}
                </h2>
              </div>
            </Grid>
          </Grid>
          <hr className="my-4" />

          <form onSubmit={handleSubmit} className="p-8">
            <Grid container spacing={3} style={{ display: "flex"}}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Secret Key"
                  name="sk"
                  value={formData.sk}
                  onChange={handleChange}
                  error={!!errors.sk}
                  helperText={errors.sk}
                  variant="standard"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Encryption Key"
                  name="key"
                  value={formData.key}
                  onChange={handleChange}
                  error={!!errors.key}
                  helperText={errors.key}
                  variant="standard"
                />
              </Grid>


              {isUpdateMode && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Payment Gateway Title"
                    name="paymentGatewayTitle"
                    value={paymentGatewayTitle}
                    onChange={handleTitleChange}
                    variant="standard"
                    // keep it read-only if you want; or make editable by setting disabled={false}
                    disabled
                  />
                </Grid>
              )}
            </Grid>

            <Grid className="flex justify-end mt-5">
              <Button
                type="submit"
                disabled={isSubmitting || (isUpdateMode && initialUpdate)}
                className={`${isUpdateMode && initialUpdate ? "!bg-gray-500 text-black cursor-not-allowed" : "!bg-black !text-white"}`}
                style={{
                  padding: "5px 24px",
                  borderRadius: "40px",
                  width: "auto",
                  textTransform: "none",
                  fontSize: "16px",
                  marginBottom: "8px",
                }}
              >
                {isSubmitting ? (isUpdateMode ? "Updating..." : "Creating...") : (isUpdateMode ? "Update" : "Create")}
              </Button>
            </Grid>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 350,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <IconButton sx={{ position: "absolute", top: 8, right: 8 }} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              width: 60,
              height: 60,
              backgroundColor: "#00C853",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "auto",
            }}
          >
            <Typography sx={{ fontSize: 40, color: "white" }}>✔</Typography>
          </Box>
          <Typography variant="h6" sx={{ mt: 2 }}>
            {isUpdateMode ? "Credentials Updated Successfully!" : "Credentials Created Successfully!"}
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 3, backgroundColor: "#000", color: "#fff", borderRadius: "40px" }}
            onClick={() => setOpen(false)}
          >
            Continue to Dashboard
          </Button>
        </Box>
      </Modal>

      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={isSuccessSnackbar ? "success" : "error"}
          sx={{
            backgroundColor: isSuccessSnackbar ? "#4caf50" : "#f44336",
            color: "#fff",
            display: "flex",
            alignItems: "center",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CredentialsInfo;